import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreditCard, ShoppingBag, DollarSign, TrendingUp, ExternalLink, Users, Truck, Wallet, RefreshCw, Coins } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [marketplaceOrders, setMarketplaceOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, marketOrdersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/admin/marketplace/orders`)
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
      setMarketplaceOrders(marketOrdersRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-HT', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-HT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statCards = [
    {
      title: "Total Kat",
      value: stats?.total_cards || 0,
      icon: CreditCard,
      color: "bg-blue-500"
    },
    {
      title: "Kat Vann",
      value: stats?.sold_cards || 0,
      icon: ShoppingBag,
      color: "bg-purple-500"
    },
    {
      title: "Revni Amazon",
      value: `${formatPrice(stats?.amazon_revenue_htg || 0)} HTG`,
      icon: ExternalLink,
      color: "bg-[#FF9900]"
    },
    {
      title: "Revni Marketplace",
      value: `${formatPrice(stats?.marketplace_system_rev || 0)} HTG`,
      icon: DollarSign,
      color: "bg-amber-600"
    },
    {
      title: "Machann Earned",
      value: `${formatPrice(stats?.marketplace_seller_rev || 0)} HTG`,
      icon: TrendingUp,
      color: "bg-emerald-500"
    },
    {
      title: "Total Machann",
      value: stats?.total_sellers || 0,
      icon: Users,
      color: "bg-green-500"
    },
    {
      title: "Pwodwi Market",
      value: stats?.total_products || 0,
      icon: ShoppingBag,
      color: "bg-indigo-500"
    },
    {
      title: "Total Chofè",
      value: stats?.total_drivers || 0,
      icon: Truck,
      color: "bg-red-500"
    },
    {
        title: "Revni Meru",
        value: `${formatPrice(stats?.meru_recharge_rev_htg || 0)} HTG`,
        icon: Wallet,
        color: "bg-green-700"
    },
    {
        title: "Rechaj Atant",
        value: stats?.pending_meru_requests || 0,
        icon: RefreshCw,
        color: "bg-orange-500"
    },
    {
        title: "Revni Crypto",
        value: `${formatPrice(stats?.crypto_recharge_rev_htg || 0)} HTG`,
        icon: Coins,
        color: "bg-teal-600"
    },
    {
        title: "Crypto Atant",
        value: stats?.pending_crypto_requests || 0,
        icon: Coins,
        color: "bg-teal-800"
    },
    {
        title: "Kliyan",
        value: stats?.total_users || 0,
        icon: Users,
        color: "bg-indigo-600"
    },
    {
        title: "Vizitè (Sit La)",
        value: stats?.total_visitors || 0,
        icon: ExternalLink,
        color: "bg-cyan-500"
    }
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <h1 className="text-3xl font-bold text-[#232F3E] mb-8">Dashboard</h1>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" data-testid="loading-spinner"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {statCards.map((stat, index) => (
                <Card key={index} className="border-0 shadow-lg" data-testid={`stat-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                        <p className="text-2xl font-bold text-[#232F3E]">{stat.value}</p>
                      </div>
                      <div className={`${stat.color} w-12 h-12 rounded-xl flex items-center justify-center`}>
                        <stat.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Marketplace Orders Section */}
            <div className="my-10 space-y-10">
                <Card className="border-0 shadow-2xl rounded-[35px] overflow-hidden bg-white">
                <CardHeader className="bg-gray-50/50 p-8 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-4 text-2xl font-black italic tracking-tighter">
                        <div className="p-3 bg-[#FF9900]/10 rounded-2xl">
                        <ShoppingBag className="w-6 h-6 text-[#FF9900]" />
                        </div>
                        Kòmand <span className="text-[#FF9900]">Marketplace</span>
                    </CardTitle>
                    <span className="bg-green-100 text-green-700 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm">
                        {marketplaceOrders.length} Komann Total
                    </span>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {marketplaceOrders.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full admin-table">
                        <thead>
                            <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                            <th className="py-5 px-8 text-left">Pwodwi</th>
                            <th className="py-5 px-8 text-left">Kliyan</th>
                            <th className="py-5 px-8 text-left">Machann</th>
                            <th className="py-5 px-8 text-left">Total Peye</th>
                            <th className="py-5 px-8 text-left">Pou Machann</th>
                            <th className="py-5 px-8 text-left">Pou Sistèm</th>
                            <th className="py-5 px-8 text-left">Estati</th>
                            <th className="py-5 px-8 text-left">Dat</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {marketplaceOrders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-50/80 transition-colors">
                                <td className="py-5 px-8">
                                <div className="flex flex-col">
                                    <span className="font-black text-[#232F3E] text-sm tracking-tight">{order.product_title}</span>
                                    <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">ID: {order.id.slice(0, 8)}</span>
                                </div>
                                </td>
                                <td className="py-5 px-8">
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-gray-700">{order.buyer_phone}</span>
                                </div>
                                </td>
                                <td className="py-5 px-8">
                                <span className="px-3 py-1 bg-gray-100 rounded-lg text-[10px] font-black text-gray-500 uppercase tracking-widest">{order.seller_id?.slice(0, 8)}</span>
                                </td>
                                <td className="py-5 px-8 font-black text-gray-900">
                                {formatPrice(order.price_htg)} HTG
                                </td>
                                <td className="py-5 px-8 font-black text-green-600">
                                {formatPrice(order.seller_amount_htg || 0)} HTG
                                </td>
                                <td className="py-5 px-8 font-black text-[#FF9900]">
                                {formatPrice(order.commission_htg || 0)} HTG
                                </td>
                                <td className="py-5 px-8">
                                <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm ${
                                    order.payment_status === 'completed'
                                    ? 'bg-green-500 text-white'
                                    : 'bg-amber-500 text-white'
                                }`}>
                                    {order.payment_status === 'completed' ? 'Peye' : 'Atant'}
                                </span>
                                </td>
                                <td className="py-5 px-8 text-[11px] font-bold text-gray-400 uppercase">
                                {formatDate(order.created_at)}
                                </td>
                            </tr>
                            ))}
                        </tbody>
                        </table>
                    </div>
                    ) : (
                    <div className="text-center py-24 text-gray-400">
                        <ShoppingBag className="w-20 h-20 mx-auto mb-6 opacity-10" />
                        <p className="font-black uppercase tracking-widest text-[11px]">Pa gen okenn komann marketplace ankò</p>
                    </div>
                    )}
                </CardContent>
                </Card>
            </div>

            {/* Recent Orders */}
            <Card className="border-0 shadow-lg" data-testid="recent-orders-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#FF9900]" />
                  Dènye Kòmand yo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentOrders.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full admin-table">
                      <thead>
                        <tr className="border-b">
                          <th className="py-3 px-4">ID</th>
                          <th className="py-3 px-4">Valè</th>
                          <th className="py-3 px-4">Pri</th>
                          <th className="py-3 px-4">Telefòn</th>
                          <th className="py-3 px-4">Estati</th>
                          <th className="py-3 px-4">Dat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentOrders.map((order) => (
                          <tr key={order.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-mono text-xs">{order.id.slice(0, 8)}...</td>
                            <td className="py-3 px-4 font-bold">${order.denomination}</td>
                            <td className="py-3 px-4">{formatPrice(order.price_htg)} HTG</td>
                            <td className="py-3 px-4">{order.customer_phone}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${order.payment_status === 'completed'
                                ? 'status-completed'
                                : 'status-pending'
                                }`}>
                                {order.payment_status === 'completed' ? 'Konplete' : 'An Atant'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500">
                              {formatDate(order.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Pa gen kòmand ankò</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

